/**
 * DM-7185: decision logic for the website deletion-request queue.
 *
 * Split out for the reason broadcasts.logic.ts is: apps/admin-ui is never
 * ESLint'd or complexity-checked in CI and its jest suite matches `.ts` only,
 * so pure logic in a sibling file is the one part of a page that can be tested.
 */

import { formatWhen, waitingPeriodNote, type DeletionLookupState } from './userDeletion.logic';
import type {
  AccountDeletionWebRequestItem,
  AccountDeletionWebRequestStatus,
  UserDeletionState,
} from '../api/adminOps';
import type { ChipTone } from '../components/ui/StatusChip';

/** In lifecycle order, which is also the order the chips are shown in. */
export const STATUS_FILTERS: readonly AccountDeletionWebRequestStatus[] = [
  'PENDING_CONFIRMATION',
  'CONFIRMED',
  'COMPLETED',
  'EXPIRED',
  'CANCELLED',
];

export type StatusFilter = AccountDeletionWebRequestStatus | 'all';

const STATUS_CHIPS: Record<
  AccountDeletionWebRequestStatus,
  { label: string; tone: ChipTone; hint: string }
> = {
  PENDING_CONFIRMATION: {
    label: 'Awaiting confirmation',
    tone: 'neutral',
    hint: 'The requester has not yet opened the emailed confirmation link. Nothing is scheduled.',
  },
  // `warn`, not `ok`: a confirmed request is an account on its way to being
  // deleted, and is the only status an operator can still act on.
  CONFIRMED: {
    label: 'Confirmed — deletion scheduled',
    tone: 'warn',
    hint: 'The requester confirmed from the emailed link and the account is in its waiting period.',
  },
  COMPLETED: {
    label: 'Completed',
    tone: 'ok',
    hint: 'The deletion this request opened has been carried out.',
  },
  EXPIRED: {
    label: 'Expired',
    tone: 'neutral',
    hint: 'The confirmation link lapsed before it was opened. Nothing was scheduled.',
  },
  CANCELLED: {
    label: 'Cancelled',
    tone: 'neutral',
    hint: 'The deletion this request opened was cancelled by the donor or an operator.',
  },
};

export function statusChip(status: string): { label: string; tone: ChipTone; hint: string } {
  return (
    STATUS_CHIPS[status as AccountDeletionWebRequestStatus] ?? {
      label: status,
      tone: 'neutral',
      hint: '',
    }
  );
}

export function filterLabel(filter: StatusFilter): string {
  return filter === 'all' ? 'All' : statusChip(filter).label;
}

/** The list params for a filter: `all` sends no status at all. */
export function queueParams(filter: StatusFilter): { status?: AccountDeletionWebRequestStatus } {
  return filter === 'all' ? {} : { status: filter };
}

export function emptyHint(filter: StatusFilter): string {
  return filter === 'all'
    ? 'Nobody has asked for their account to be deleted from the website.'
    : `No requests with status "${statusChip(filter).label}".`;
}

const SOURCE_LABELS: Record<string, string> = { web: 'Website form' };

export interface RequestDisplay {
  /** What the requester typed, or a placeholder; never trusted as markup. */
  readonly requesterName: string;
  readonly hasName: boolean;
  readonly message: string;
  readonly hasMessage: boolean;
  readonly source: string;
  readonly submitted: string;
  readonly confirmed: string;
  /** Only meaningful while unconfirmed; '—' once the link has been used. */
  readonly linkExpires: string;
  readonly waitingPeriodEnds: string;
  readonly waitingPeriodNote: string;
}

function typedText(value: string | null): string {
  return (value ?? '').trim();
}

/** One queue row as the strings the table and drawer show. */
export function requestDisplay(item: AccountDeletionWebRequestItem, now: Date): RequestDisplay {
  const name = typedText(item.name);
  const message = typedText(item.message);
  return {
    requesterName: name === '' ? 'Not given' : name,
    hasName: name !== '',
    message: message === '' ? 'No message was left.' : message,
    hasMessage: message !== '',
    source: SOURCE_LABELS[item.source] ?? item.source,
    submitted: formatWhen(item.created_at),
    confirmed: formatWhen(item.confirmed_at, 'Not confirmed'),
    linkExpires: item.confirmed_at === null ? formatWhen(item.expires_at) : '—',
    waitingPeriodEnds: formatWhen(item.cooling_off_until, 'Not scheduled'),
    waitingPeriodNote: waitingPeriodNote(item.cooling_off_until, now),
  };
}

export interface CancelGate {
  /** Whether to render the cancel control at all. */
  readonly canCancel: boolean;
  /** What the viewer is told, whether or not they can act. */
  readonly note: string;
}

/**
 * Whether the drawer may offer "Cancel deletion" for one queue row.
 *
 * The row alone is not enough. Its status is derived when the queue was read
 * and carries no Phase 4 flag, so the account's live deletion state is read
 * too, and the two must agree: the cancel route is keyed on the USER, so
 * offering it while the account's active deletion is a different request from
 * the one on screen would cancel something the operator never looked at.
 */
export function cancelGate(
  item: AccountDeletionWebRequestItem,
  lookup: DeletionLookupState,
  state: UserDeletionState | null,
  viewerCanWrite: boolean
): CancelGate {
  if (item.status !== 'CONFIRMED') {
    return { canCancel: false, note: statusChip(item.status).hint };
  }
  if (lookup === 'idle' || lookup === 'loading') {
    return { canCancel: false, note: 'Checking whether this deletion can still be cancelled…' };
  }
  if (lookup === 'error' || state === null) {
    return {
      canCancel: false,
      note: 'The account’s deletion state could not be loaded, so it is not safe to offer a cancellation. Retry, or open the account’s enforcement page.',
    };
  }
  if (state.phase_4_started) {
    return {
      canCancel: false,
      note: 'Deletion is already being carried out. It can no longer be cancelled by anyone.',
    };
  }
  if (!state.active || state.request === null) {
    return {
      canCancel: false,
      note: 'This account no longer has a deletion scheduled. Refresh the queue to see its current status.',
    };
  }
  // A confirmed row can lack its link: confirming opens the deletion and THEN
  // records its id on this row, and that second write can fail. Without the id
  // there is nothing tying this row to the account's active deletion, which
  // may be a different one entirely -- and the cancel route is keyed on the
  // user, so it would cancel whichever is active.
  if (item.deletion_request_id === null) {
    return {
      canCancel: false,
      note: 'This request is not linked to a deletion, so it cannot be matched to the one now scheduled for the account. Review the account on its enforcement page before cancelling anything.',
    };
  }
  if (state.request.id !== item.deletion_request_id) {
    return {
      canCancel: false,
      note: 'The account’s active deletion is not the one this request opened. Review it on the account’s enforcement page before cancelling anything.',
    };
  }
  if (!viewerCanWrite) {
    return {
      canCancel: false,
      note: 'Your role has read-only access — cancelling a deletion requires the admin or admin-operator role.',
    };
  }
  return {
    canCancel: true,
    note: 'The waiting period is still running, so this deletion can be cancelled.',
  };
}
